import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { interestedPartiesApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { toneFromMap, type Tone } from '../constants/tone';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CIAItem = 'Confidentiality' | 'Integrity' | 'Availability';

type InterestedParty = {
  partyId: string;
  name: string;
  category?: string;
  partyType?: string;
  requirementDescription?: string;
  requirementType?: string;
  sourceReference?: string;
  relevantToIsms?: string;
  ciaImpact?: CIAItem[];
  owner?: string;
  monitoringMethod?: string;
  reviewFrequency?: string;
  complianceStatus?: string;
  lastReviewDate?: string;
  nextReviewDate?: string;
  notes?: string;
};

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const CATEGORIES = [
  'Regulator', 'Customer', 'Supplier', 'Partner', 'Employee',
  'Shareholder', 'Government', 'Community', 'Board / Governance', 'Other',
];

const COMPLIANCE_STATUSES = [
  'Compliant', 'Partially Compliant', 'Non-Compliant', 'Under Review', 'Not Assessed',
];

// ---------------------------------------------------------------------------
// Chip tone helpers — return one of np-tone-{success,warning,danger,info,neutral,primary}
// ---------------------------------------------------------------------------

// Genuinely domain-specific vocabulary (compliance status, internal/external, CIA triad,
// ISMS-relevance) - no cross-page equivalent to converge onto. Rewritten onto the shared
// toneFromMap() helper for implementation-shape consistency only; every tone below is
// unchanged from before.
const STATUS_TONE: Record<string, Tone> = {
  Compliant: 'success',
  'Partially Compliant': 'warning',
  'Non-Compliant': 'danger',
  'Under Review': 'info',
};
const getStatusTone = (status?: string): Tone => toneFromMap(STATUS_TONE, status, 'neutral');

const TYPE_TONE: Record<string, Tone> = {
  Internal: 'primary',
  External: 'neutral',
};
const getTypeTone = (type?: string): Tone => toneFromMap(TYPE_TONE, type, 'neutral');

const CIA_TONE: Record<CIAItem, Tone> = {
  Confidentiality: 'primary',
  Integrity: 'success',
  Availability: 'warning',
};
const getCIATone = (item: CIAItem): Tone => toneFromMap(CIA_TONE, item, 'neutral');

const RELEVANCE_TONE: Record<string, Tone> = {
  Yes: 'success',
  No: 'neutral',
  Partial: 'warning',
};
const getRelevanceTone = (val?: string): Tone => toneFromMap(RELEVANCE_TONE, val, 'neutral');

// ---------------------------------------------------------------------------
// PDF export
// ---------------------------------------------------------------------------

const exportPDF = async (
  parties: InterestedParty[],
  stats: { total: number; compliant: number; nonCompliant: number; ismsRelevant: number; notAssessed: number },
  setExporting: (v: boolean) => void,
) => {
  setExporting(true);
  try {
    const e = (s?: string | null) =>
      (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const genDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const fileDate = new Date().toISOString().slice(0, 10);

    const statusColor = (s?: string) => {
      switch (s) {
        case 'Compliant':            return '#15803d';
        case 'Partially Compliant':  return '#d97706';
        case 'Non-Compliant':        return '#dc2626';
        case 'Under Review':         return '#0891b2';
        default:                     return '#6b7280';
      }
    };
    const statusBg = (s?: string) => {
      switch (s) {
        case 'Compliant':            return '#dcfce7';
        case 'Partially Compliant':  return '#fef3c7';
        case 'Non-Compliant':        return '#fee2e2';
        case 'Under Review':         return '#cffafe';
        default:                     return '#f3f4f6';
      }
    };

    const sc = (label: string, value: number, color: string) =>
      `<div style="display:inline-block;width:calc(20% - 8px);margin:0 4px 8px 0;padding:10px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;vertical-align:top;text-align:center;">` +
      `<span style="font-size:20px;font-weight:700;color:${color};">${value}</span><br>` +
      `<span style="font-size:11px;color:#6b7280;">${label}</span></div>`;

    const field = (label: string, value: string) =>
      `<p style="margin:0 0 10px 0;">` +
      `<span style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">${label}</span><br>` +
      `<span style="font-size:13px;color:#111827;line-height:1.5;">${value || '\u2014'}</span></p>`;

    const fieldRow = (f1: string, f2: string) =>
      `<table style="width:100%;border-collapse:collapse;"><tr>` +
      `<td style="width:50%;padding-right:16px;vertical-align:top;">${f1}</td>` +
      `<td style="width:50%;vertical-align:top;">${f2}</td>` +
      `</tr></table>`;

    const cards = parties.map(p => {
      const sc_clr = statusColor(p.complianceStatus);
      const sc_bg  = statusBg(p.complianceStatus);
      const lrd = p.lastReviewDate
        ? new Date(p.lastReviewDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '\u2014';
      return `<div style="border:1px solid #e5e7eb;border-radius:8px;margin-bottom:16px;">` +
        `<table style="width:100%;border-collapse:collapse;background:#f9fafb;border-bottom:1px solid #e5e7eb;"><tr>` +
        `<td style="padding:12px 16px;vertical-align:middle;">` +
        `<span style="font-size:14px;font-weight:700;color:#111827;">${e(p.name)}</span>` +
        (p.partyType ? `<span style="margin-left:8px;padding:2px 10px;border-radius:12px;font-size:11px;font-weight:600;background:#ede9fe;color:#5b21b6;">${e(p.partyType)}</span>` : '') +
        `</td>` +
        `<td style="padding:12px 16px;vertical-align:middle;text-align:right;white-space:nowrap;">` +
        `<span style="padding:3px 12px;border-radius:12px;font-size:11px;font-weight:600;background:${sc_bg};color:${sc_clr};">${e(p.complianceStatus || 'Not Assessed')}</span>` +
        `</td></tr></table>` +
        `<div style="padding:16px 20px;">` +
        fieldRow(field('Category', e(p.category) || '\u2014'), field('Requirement Type', e(p.requirementType) || '\u2014')) +
        fieldRow(field('Owner', e(p.owner) || '\u2014'), field('Review Frequency', e(p.reviewFrequency) || '\u2014')) +
        fieldRow(field('ISMS Relevant', e(p.relevantToIsms) || '\u2014'), field('Last Review', lrd)) +
        (p.requirementDescription ? field('Requirements', e(p.requirementDescription.substring(0, 300)) + (p.requirementDescription.length > 300 ? '\u2026' : '')) : '') +
        `</div></div>`;
    }).join('');

    const html =
      `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;background:#ffffff;color:#111827;width:860px;padding:40px;">` +
      `<div style="padding:32px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:32px;">` +
      `<h1 style="font-size:28px;font-weight:800;color:#111827;margin:0 0 12px 0;">Interested Parties Register</h1>` +
      `<div style="margin-bottom:20px;">` +
      `<span style="padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;color:#5b21b6;background:#ede9fe;">ISO 27001:2022 \u2014 Clause 4.2</span>` +
      `<span style="font-size:13px;color:#6b7280;margin-left:12px;">Generated ${genDate}</span>` +
      `</div>` +
      sc('Total', stats.total, '#111827') +
      sc('Compliant', stats.compliant, '#15803d') +
      sc('Non-Compliant', stats.nonCompliant, '#dc2626') +
      sc('ISMS Relevant', stats.ismsRelevant, '#0891b2') +
      sc('Not Assessed', stats.notAssessed, '#6b7280') +
      `</div>` +
      cards +
      `<table style="width:100%;border-collapse:collapse;margin-top:32px;"><tr>` +
      `<td style="padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;">Interested Parties Register \u00b7 ISO/IEC 27001:2022 \u00a74.2</td>` +
      `<td style="padding-top:16px;border-top:1px solid #e5e7eb;text-align:right;font-size:11px;color:#9ca3af;">Generated ${genDate}</td>` +
      `</tr></table>` +
      `</div>`;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:absolute;top:99999px;left:0;width:860px;';
    wrapper.innerHTML = html;
    document.body.appendChild(wrapper);
    void wrapper.getBoundingClientRect();
    const fullH = wrapper.scrollHeight;
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(wrapper, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', width: 860, height: fullH, windowWidth: 860, windowHeight: fullH });
    document.body.removeChild(wrapper);
    const margin = 10, printW = 190;
    const imgH = canvas.height / 2;
    const ratio = printW / (canvas.width / 2);
    const docH = imgH * ratio + margin * 2;
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [210, Math.max(docH, 210.1)] });
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, printW, imgH * ratio);
    pdf.save(`interested-parties-${fileDate}.pdf`);
  } catch (err) {
    console.error(err);
  } finally {
    setExporting(false);
  }
};
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InterestedPartiesPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();
  const [exporting, setExporting] = useState(false);

  // Data
  const { data: parties = [], isLoading } = useQuery<InterestedParty[]>({
    queryKey: ['interested-parties'],
    queryFn: interestedPartiesApi.getAll,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // ---------------------------------------------------------------- mutations

  const deleteMutation = useMutation({
    mutationFn: (partyId: string) => interestedPartiesApi.delete(partyId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['interested-parties'] });
      toast.success('Interested party deleted.');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const handleDelete = async (p: InterestedParty) => {
    const ok = await swal.confirmDelete(p.name || p.partyId);
    if (!ok) return;
    deleteMutation.mutate(p.partyId);
  };

  // ---------------------------------------------------------------- derived data

  const stats = useMemo(() => ({
    total: parties.length,
    compliant: parties.filter((p) => p.complianceStatus === 'Compliant').length,
    nonCompliant: parties.filter((p) => p.complianceStatus === 'Non-Compliant').length,
    notAssessed: parties.filter((p) => !p.complianceStatus || p.complianceStatus === 'Not Assessed').length,
    ismsRelevant: parties.filter((p) => p.relevantToIsms === 'Yes').length,
  }), [parties]);

  const searched = useMemo(() => {
    const q = search.toLowerCase();
    return parties.filter((p) =>
      (!q || [p.name, p.category, p.owner, p.requirementDescription]
        .some((v) => v?.toLowerCase().includes(q)))
      && (!filterCategory || p.category === filterCategory)
      && (!filterType || p.partyType === filterType)
      && (!filterStatus || p.complianceStatus === filterStatus),
    );
  }, [parties, search, filterCategory, filterType, filterStatus]);

  const { sorted: filtered, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<InterestedParty, keyof InterestedParty>(
    searched,
    (p, f) => {
      const v = p[f];
      return Array.isArray(v) ? v.join(',') : String(v ?? '');
    },
    'name',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(filtered, 25);

  // ---------------------------------------------------------------- render

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-group-line"
        title="Interested Parties"
        colour="primary"
        actions={
          <div className="d-flex gap-2">
            <button className="np-btn np-btn-ghost" onClick={() => exportPDF(filtered, stats, setExporting)} disabled={exporting}>
              {exporting ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />Generating…</> : <><i className="ri-file-pdf-line"></i>Export PDF</>}
            </button>
            <button className="np-btn np-btn-primary" onClick={() => navigate('/interested-parties/new')}>
              <i className="ri-add-line"></i>Add Party
            </button>
          </div>
        }
      />

      {/* Single card: header has stats, body has filters + table */}
      <div className="np-card">
        {/* Card header: title + stats */}
        <div className="np-card-head flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <i className="ri-group-line text-primary"></i>
            <h2 className="np-mb-0">Interested Parties</h2>
            <span className="np-chip np-tone-neutral np-tabular">{stats.total}</span>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
            {stats.compliant > 0 && (
              <span className="np-chip np-tone-success np-tabular">
                <i className="ri-shield-check-line"></i>{stats.compliant} compliant
              </span>
            )}
            {stats.nonCompliant > 0 && (
              <span className="np-chip np-tone-danger np-tabular">
                <i className="ri-error-warning-line"></i>{stats.nonCompliant} non-compliant
              </span>
            )}
            {stats.notAssessed > 0 && (
              <span className="np-chip np-tone-warning np-tabular">
                <i className="ri-time-line"></i>{stats.notAssessed} not assessed
              </span>
            )}
            {stats.ismsRelevant > 0 && (
              <span className="np-chip np-tone-info np-tabular">
                <i className="ri-focus-3-line"></i>{stats.ismsRelevant} ISMS relevant
              </span>
            )}
          </div>
        </div>

        {/* Filters toolbar */}
        <div className="np-card-body border-bottom py-3">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="np-search" style={{ maxWidth: 260 }}>
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="Search name, category, owner…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="np-icon-btn" onClick={() => setSearch('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            <select className="np-select" style={{ maxWidth: 160 }} value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select className="np-select" style={{ maxWidth: 130 }} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="">Int. / Ext.</option>
              <option>Internal</option>
              <option>External</option>
            </select>
            <select className="np-select" style={{ maxWidth: 170 }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              {COMPLIANCE_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            {(search || filterCategory || filterType || filterStatus) && (
              <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => { setSearch(''); setFilterCategory(''); setFilterType(''); setFilterStatus(''); }}>
                <i className="ri-filter-off-line"></i>Clear
              </button>
            )}
            <span className="np-sub np-mb-0 ms-auto">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Table */}
        <div className="np-card-body p-0">
          {isLoading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5 np-sub">
              <i className="ri-group-line ri-3x d-block mb-2 opacity-25"></i>
              {parties.length === 0 ? 'No interested parties yet — add one to get started.' : 'No results match your filters.'}
            </div>
          ) : (
            <NimbusTable thead={<tr>
                  {(
                    [
                      ['name', 'Interested Party'],
                      ['category', 'Category'],
                      ['partyType', 'Int./Ext.'],
                      ['requirementType', 'Req. Type'],
                      ['sourceReference', 'Source'],
                      ['relevantToIsms', 'ISMS?'],
                      ['ciaImpact', 'CIA'],
                      ['owner', 'Owner'],
                      ['reviewFrequency', 'Frequency'],
                      ['complianceStatus', 'Status'],
                    ] as [keyof InterestedParty, string][]
                  ).map(([field, label]) => (
                    <SortableTh key={field} field={field} active={sortField === field} dir={sortDir} onSort={toggleSort}>
                      {label}
                    </SortableTh>
                  ))}
                  <th style={{ width: 80 }}></th>
                </tr>}>
                {pageItems.map((p) => (
                  <tr key={p.partyId}>
                    <td>
                      <div className="np-row-title" style={{ maxWidth: 200 }}>{p.name}</div>
                      {p.requirementDescription && (
                        <div className="np-sub np-mb-0 text-truncate" style={{ maxWidth: 200 }} title={p.requirementDescription}>
                          {p.requirementDescription}
                        </div>
                      )}
                    </td>
                    <td><span className="np-chip np-tone-neutral">{p.category || '—'}</span></td>
                    <td><span className={`np-chip np-tone-${getTypeTone(p.partyType)}`}>{p.partyType || '—'}</span></td>
                    <td>{p.requirementType || <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}</td>
                    <td>
                      {p.sourceReference
                        ? <span className="np-chip np-tone-info">{p.sourceReference}</span>
                        : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                    </td>
                    <td><span className={`np-chip np-tone-${getRelevanceTone(p.relevantToIsms)}`}>{p.relevantToIsms || '—'}</span></td>
                    <td>
                      {(p.ciaImpact?.length ?? 0) > 0
                        ? p.ciaImpact!.map((c) => <span key={c} className={`np-chip np-tone-${getCIATone(c)} me-1`}>{c[0]}</span>)
                        : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                    </td>
                    <td>{p.owner || <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}</td>
                    <td>{p.reviewFrequency || <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}</td>
                    <td><span className={`np-chip np-tone-${getStatusTone(p.complianceStatus)}`}>{p.complianceStatus || 'Not Assessed'}</span></td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="np-icon-btn" title="Edit" onClick={() => navigate(`/interested-parties/${p.partyId}/edit`)}>
                          <i className="ri-pencil-line"></i>
                        </button>
                        <button className="np-icon-btn np-icon-btn-danger" title="Delete" onClick={() => handleDelete(p)}>
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </NimbusTable>
          )}
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      </div>
    </div>
  );
};
